import { useEffect, useRef, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { MdHistory } from "react-icons/md";
import { db } from "../ChatBot/firebaseconfig";
import winLogo from "../../assets/images/logo-win.svg";
import maintenanceTools from "../../assets/images/maintenance-tools.png";
import maintenanceWarningIcon from "../../assets/images/maintenance-warning.png";
import "./AppMaintenance.css";

const MAX_TIMEOUT_DELAY = 2147483647;

const isLoginPage = () =>
  window.location.pathname.replace(/\/+$/, "") === "/login";

const redirectToLogin = () => {
  if (isLoginPage()) return false;

  window.location.replace(`/login${window.location.search}`);
  return true;
};

const getDateMilliseconds = (value) => {
  const milliseconds = value?.toMillis?.();
  return Number.isFinite(milliseconds) ? milliseconds : null;
};

const formatMaintenanceDate = (milliseconds) => {
  if (!Number.isFinite(milliseconds)) return "";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(milliseconds));
};

const formatMaintenanceTime = (milliseconds) => {
  if (!Number.isFinite(milliseconds)) return "";

  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(milliseconds));
};

const formatMaintenanceMessage = (message, schedule) => {
  const formattedMessage = typeof message === "string" ? message : "";

  return formattedMessage
    .replace(
      /\{START_TIME\}/gi,
      formatMaintenanceDate(schedule.maintenanceStartTime)
    )
    .replace(
      /\{END_TIME\}/gi,
      formatMaintenanceDate(schedule.maintenanceEndTime)
    );
};

const getMaintenanceWarningSchedule = (snapshot) => {
  const schedules = snapshot.docs
    .map((maintenanceDocument) => {
      const maintenanceData = maintenanceDocument.data();
      const startTime = getDateMilliseconds(
        maintenanceData?.maintenance_warning_start_time
      );

      return !Number.isFinite(startTime)
          ? null
          : {
            key: `${maintenanceDocument.id}:${startTime}`,
            title: maintenanceData?.maintenance_title?.en,
            message: maintenanceData?.maintenance_message?.en,
            showIcon: maintenanceData?.maintenance_show_icon === true,
            maintenanceStartTime: getDateMilliseconds(
              maintenanceData?.maintenance_start_time
            ),
            maintenanceEndTime: getDateMilliseconds(
              maintenanceData?.maintenance_end_time
            ),
            startTime,
          };
    })
    .filter(Boolean);

  if (schedules.length === 0) return null;

  const now = Date.now();
  return (
    schedules
      .filter(({ startTime }) => startTime > now)
      .sort((first, second) => first.startTime - second.startTime)[0] || null
  );
};

const getMaintenanceWindowSchedule = (snapshot) => {
  const windows = snapshot.docs
    .map((maintenanceDocument) => {
      const maintenanceData = maintenanceDocument.data();
      const maintenanceStartTime = getDateMilliseconds(
        maintenanceData?.maintenance_start_time
      );
      const maintenanceEndTime = getDateMilliseconds(
        maintenanceData?.maintenance_end_time
      );

      return !Number.isFinite(maintenanceStartTime) ||
        !Number.isFinite(maintenanceEndTime) ||
        maintenanceEndTime <= maintenanceStartTime
        ? null
        : {
            key: `${maintenanceDocument.id}:${maintenanceStartTime}:${maintenanceEndTime}`,
            detailMessage: maintenanceData?.maintenance_detailMessage?.en,
            maintenanceStartTime,
            maintenanceEndTime,
          };
    })
    .filter(Boolean);

  if (windows.length === 0) return null;

  const now = Date.now();
  const activeWindow = windows
    .filter(
      ({ maintenanceStartTime, maintenanceEndTime }) =>
        maintenanceStartTime <= now && maintenanceEndTime > now
    )
    .sort(
      (first, second) => first.maintenanceEndTime - second.maintenanceEndTime
    )[0];

  if (activeWindow) return activeWindow;

  return (
    windows
      .filter(({ maintenanceStartTime }) => maintenanceStartTime > now)
      .sort(
        (first, second) =>
          first.maintenanceStartTime - second.maintenanceStartTime
      )[0] || null
  );
};

const AppMaintenance = () => {
  const [maintenanceWarning, setMaintenanceWarning] = useState(null);
  const [maintenanceWindow, setMaintenanceWindow] = useState(null);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const maintenanceTimer = useRef(null);
  const maintenanceWindowTimer = useRef(null);
  const dismissedMaintenanceKey = useRef(null);

  useEffect(() => {
    const clearMaintenanceTimer = () => {
      if (maintenanceTimer.current) {
        clearTimeout(maintenanceTimer.current);
        maintenanceTimer.current = null;
      }
    };

    const clearMaintenanceWindowTimer = () => {
      if (maintenanceWindowTimer.current) {
        clearTimeout(maintenanceWindowTimer.current);
        maintenanceWindowTimer.current = null;
      }
    };

    const showMaintenanceWarning = (schedule) => {
      if (dismissedMaintenanceKey.current !== schedule.key) {
        setMaintenanceWarning(schedule);
      }
    };

    const scheduleMaintenanceWindow = (schedule) => {
      if (!schedule) {
        setMaintenanceWindow(null);
        return;
      }

      const showOrScheduleMaintenanceWindow = () => {
        const now = Date.now();

        if (now < schedule.maintenanceStartTime) {
          maintenanceWindowTimer.current = setTimeout(
            showOrScheduleMaintenanceWindow,
            Math.min(schedule.maintenanceStartTime - now, MAX_TIMEOUT_DELAY)
          );
          return;
        }

        if (now >= schedule.maintenanceEndTime) {
          setMaintenanceWindow(null);
          setMaintenanceWarning(null);
          return;
        }

        if (!isLoginPage()) {
          redirectToLogin();
          return;
        }

        setMaintenanceWarning(null);
        setMaintenanceWindow(schedule);
        maintenanceWindowTimer.current = setTimeout(
          showOrScheduleMaintenanceWindow,
          Math.min(schedule.maintenanceEndTime - now, MAX_TIMEOUT_DELAY)
        );
      };

      showOrScheduleMaintenanceWindow();
    };

    const unsubscribe = onSnapshot(
      collection(db, "App_Maintenance"),
      (snapshot) => {
        clearMaintenanceTimer();
        clearMaintenanceWindowTimer();

        const warningSchedule = getMaintenanceWarningSchedule(snapshot);
        const windowSchedule = getMaintenanceWindowSchedule(snapshot);

        if (!warningSchedule) {
          setMaintenanceWarning(null);
        } else {
          const showAtStartTime = () => {
            const remainingTime = warningSchedule.startTime - Date.now();

            if (remainingTime > 0) {
              maintenanceTimer.current = setTimeout(
                showAtStartTime,
                Math.min(remainingTime, MAX_TIMEOUT_DELAY)
              );
              return;
            }

            showMaintenanceWarning(warningSchedule);
          };

          showAtStartTime();
        }

        scheduleMaintenanceWindow(windowSchedule);
      },
      (error) => {
        console.error("Unable to listen for maintenance warnings:", error);
      }
    );

    return () => {
      clearMaintenanceTimer();
      clearMaintenanceWindowTimer();
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!maintenanceWindow) return undefined;

    setCurrentTime(Date.now());
    const progressTimer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      clearInterval(progressTimer);
      document.body.style.overflow = previousOverflow;
    };
  }, [maintenanceWindow]);

  const maintenanceDuration =
    maintenanceWindow?.maintenanceEndTime -
    maintenanceWindow?.maintenanceStartTime;
  const maintenanceProgress = maintenanceWindow
    ? Math.min(
        100,
        Math.max(
          0,
          ((currentTime - maintenanceWindow.maintenanceStartTime) /
            maintenanceDuration) *
            100
        )
      )
    : 0;

  return (
    <>
      {maintenanceWarning && !maintenanceWindow && (
        <div className="maintenance-warning-overlay">
          <div
            className="maintenance-warning-popup"
            role="alertdialog"
            aria-modal="true"
            aria-label="Maintenance warning"
          >
            <button
              type="button"
              className="maintenance-warning-close"
              onClick={() => {
                dismissedMaintenanceKey.current = maintenanceWarning.key;
                setMaintenanceWarning(null);
              }}
              aria-label="Close maintenance warning"
            >
              &times;
            </button>
            <div className="maintenance-warning-content">
              {maintenanceWarning.showIcon && (
                <img
                  src={maintenanceWarningIcon}
                  alt=""
                  className="maintenance-warning-icon"
                />
              )}
              <div className="maintenance-warning-copy">
                {maintenanceWarning.title && (
                  <h2>{maintenanceWarning.title}</h2>
                )}
                <p>
                  {formatMaintenanceMessage(
                    maintenanceWarning.message,
                    maintenanceWarning
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {maintenanceWindow && (
        <div className="maintenance-window-overlay">
          <div
            className="maintenance-window-popup"
            role="alertdialog"
            aria-modal="true"
            aria-label="Maintenance in progress"
          >
            <img
              src={winLogo}
              alt="WIN"
              className="maintenance-window-logo"
            />

            <div className="maintenance-window-tools" aria-hidden="true">
              <img src={maintenanceTools} alt="" />
            </div>

            <h2>Under Maintenance</h2>
            <p className="maintenance-window-subtitle">
              We're making things better
            </p>

            <div className="maintenance-window-detail">
              {formatMaintenanceMessage(
                maintenanceWindow.detailMessage,
                maintenanceWindow
              )}
            </div>

            <div
              className="maintenance-window-progress-section"
              aria-label={`${Math.round(maintenanceProgress)}% complete`}
            >
              <div className="maintenance-window-progress-track">
                <div
                  className="maintenance-window-progress-fill"
                  style={{ width: `${maintenanceProgress}%` }}
                />
                <div className="maintenance-window-progress-times">
                  <span>
                    {formatMaintenanceTime(
                      maintenanceWindow.maintenanceStartTime
                    )}
                  </span>
                  <span>
                    {formatMaintenanceTime(
                      maintenanceWindow.maintenanceEndTime
                    )}
                  </span>
                </div>
              </div>
              <div className="maintenance-window-progress-label">
                {Math.round(maintenanceProgress)}% complete
              </div>
            </div>

            <div className="maintenance-window-footer">
              <div className="maintenance-window-history-icon" aria-hidden="true">
                <MdHistory aria-hidden="true" />
              </div>
              <div>We'll be back soon!</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AppMaintenance;
